/**
 * Playlist duration limit validation utilities.
 *
 * Matches SCIO 1 behavior for validating items before adding to playlists:
 * - Single asset: auto-clamp duration to fit remaining space
 * - Multi-asset/folder: block if total would exceed limit
 * - Nested playlist: block if would exceed limit (no auto-adjust)
 *
 * Reference: scio1/imports/ui/components/contentManagement/playlistManagement/utils/validate.js
 */

import type { Playlist, PlaylistAsset } from '@scio3/shared-types';
import { calculateTotalDuration } from './duration-helpers';
import { MIN_DURATION } from './playlist-constants';

export interface DurationValidationResult {
  /** Whether the items can be added (with or without adjustment). */
  valid: boolean;
  /** If valid=false, the i18n key for the error message. */
  errorKey?: string;
  /** i18n interpolation params (e.g., { durationLimit: 100 }). */
  errorParams?: Record<string, unknown>;
  /** For single-asset adds, the adjusted duration if clamping was needed. */
  adjustedDuration?: number;
}

/**
 * Validate adding assets to a playlist with a duration limit.
 *
 * Behavior (matching SCIO 1):
 * - If playlist has no durationLimit or durationLimit <= 0, always valid
 * - If playlist is already at or over the limit, block with error
 * - Single asset: auto-clamp duration to fit remaining space (valid with adjustedDuration)
 * - Multiple assets or folder: block if total would exceed limit
 *
 * @param playlist - The target playlist
 * @param localItems - Current local items in the editor (for accurate totalDuration)
 * @param itemsToAdd - The items being added (with their durations)
 * @param isFolder - Whether this is a bulk folder add (treated as multi-asset)
 */
export function validateAssetAdd(
  playlist: Playlist | null | undefined,
  localItems: PlaylistAsset[],
  itemsToAdd: PlaylistAsset[],
  isFolder = false,
): DurationValidationResult {
  if (!playlist) {
    return { valid: true };
  }

  const durationLimit = playlist.options?.durationLimit;
  if (!durationLimit || durationLimit <= 0) {
    return { valid: true };
  }

  const currentTotal = calculateTotalDuration(localItems);
  const itemsDuration = itemsToAdd.reduce((sum, item) => sum + (item.duration ?? 0), 0);

  // Already at or over limit
  if (currentTotal >= durationLimit) {
    return {
      valid: false,
      errorKey: 'editSectionDurationLimit',
      errorParams: { durationLimit },
    };
  }

  const wouldExceed = currentTotal + itemsDuration > durationLimit;

  // Multi-asset or folder: block if would exceed
  if (itemsToAdd.length > 1 || isFolder) {
    if (wouldExceed) {
      return {
        valid: false,
        errorKey: 'editSectionDurationLimit',
        errorParams: { durationLimit },
      };
    }
    return { valid: true };
  }

  // Single asset: auto-clamp duration to fit
  if (itemsToAdd.length === 1 && wouldExceed) {
    const remainingSpace = durationLimit - currentTotal;
    // Round to 3 decimal places (matching item duration precision in DB)
    const adjustedDuration = Math.round(remainingSpace * 1000) / 1000;
    // Ensure at least MIN_DURATION (matches SCIO 1 behavior)
    if (adjustedDuration < MIN_DURATION) {
      return {
        valid: false,
        errorKey: 'editSectionDurationLimit',
        errorParams: { durationLimit },
      };
    }
    return {
      valid: true,
      adjustedDuration,
    };
  }

  return { valid: true };
}

/**
 * Validate multiple nested playlists being added.
 *
 * @param playlist - The target (parent) playlist
 * @param localItems - Current local items in the editor
 * @param nestedDurations - Array of durations for each nested playlist
 */
export function validateMultipleNestedPlaylistAdd(
  playlist: Playlist | null | undefined,
  localItems: PlaylistAsset[],
  nestedDurations: number[],
): DurationValidationResult {
  if (!playlist) {
    return { valid: true };
  }

  const durationLimit = playlist.options?.durationLimit;
  if (!durationLimit || durationLimit <= 0) {
    return { valid: true };
  }

  const currentTotal = calculateTotalDuration(localItems);
  const totalNestedDuration = nestedDurations.reduce((sum, d) => sum + d, 0);

  // Already at or over limit
  if (currentTotal >= durationLimit) {
    return {
      valid: false,
      errorKey: 'editSectionDurationLimit',
      errorParams: { durationLimit },
    };
  }

  // Would exceed limit
  if (currentTotal + totalNestedDuration > durationLimit) {
    return {
      valid: false,
      errorKey: 'editSectionDurationLimit',
      errorParams: { durationLimit },
    };
  }

  return { valid: true };
}
